import { Controller } from "@hotwired/stimulus"

/**
 * AdminTargetedMessageController - Handles targeted admin messaging form
 *
 * Features:
 * - Audience type selection (all_users, by_city, by_district)
 * - Cascading city/district selection
 * - Real-time recipient count preview (debounced)
 * - Live message preview
 *
 * Usage:
 * <div data-controller="admin-targeted-message"
 *      data-admin-targeted-message-cities-url-value="/admin/admin_messages/cities"
 *      data-admin-targeted-message-districts-url-value="/admin/admin_messages/districts"
 *      data-admin-targeted-message-preview-url-value="/admin/admin_messages/preview_recipients">
 */
export default class extends Controller {
  static targets = [
    "citySection",
    "districtSection",
    "citySelect",
    "districtSelect",
    "recipientCount",
    "previewTitle",
    "previewBody",
    "titleInput",
    "bodyInput",
    "submitButton"
  ]

  static values = {
    citiesUrl: String,
    districtsUrl: String,
    previewUrl: String,
    debounceMs: { type: Number, default: 300 }
  }

  connect() {
    this.debounceTimer = null
    this.loadCities()
    this.updateRecipientCount()
    this.initializeSelectedStates()
  }

  // Initialize selected states for radio buttons and checkboxes on page load
  initializeSelectedStates() {
    // Initialize radio button selected states
    this.element.querySelectorAll('input[name="audience_type"]').forEach(radio => {
      const label = radio.closest('label')
      if (label) {
        label.classList.toggle('selected', radio.checked)
      }
    })

    // Initialize checkbox selected states
    this.element.querySelectorAll('input[type="checkbox"][name="send_push"], input[type="checkbox"][name="send_in_app"]').forEach(checkbox => {
      const label = checkbox.closest('label')
      if (label) {
        label.classList.toggle('selected', checkbox.checked)
      }
    })
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  // Load cities list on connect
  async loadCities() {
    if (!this.hasCitySelectTarget) return

    try {
      const response = await fetch(this.citiesUrlValue)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const cities = await response.json()

      // Clear existing options and rebuild using DOM API (XSS safe)
      this.citySelectTarget.innerHTML = ''
      cities.forEach(city => {
        const option = document.createElement('option')
        option.value = city
        option.textContent = city
        this.citySelectTarget.appendChild(option)
      })
    } catch (error) {
      console.error('Failed to load cities:', error)
    }
  }

  // Handle audience type change
  changeAudienceType(event) {
    const type = event.target.value

    // Update selected state for all radio buttons
    this.element.querySelectorAll('input[name="audience_type"]').forEach(radio => {
      const label = radio.closest('label')
      if (label) {
        label.classList.toggle('selected', radio.checked)
      }
    })

    // Hide all sections first
    if (this.hasCitySectionTarget) {
      this.citySectionTarget.classList.add('hidden')
    }
    if (this.hasDistrictSectionTarget) {
      this.districtSectionTarget.classList.add('hidden')
    }

    // Show relevant section based on type
    switch (type) {
      case 'by_city':
        if (this.hasCitySectionTarget) {
          this.citySectionTarget.classList.remove('hidden')
        }
        break
      case 'by_district':
        if (this.hasCitySectionTarget) {
          this.citySectionTarget.classList.remove('hidden')
        }
        if (this.hasDistrictSectionTarget) {
          this.districtSectionTarget.classList.remove('hidden')
        }
        break
    }

    this.debouncedUpdateRecipientCount()
  }

  // Handle checkbox toggle for delivery options
  toggleDeliveryOption(event) {
    const checkbox = event.target
    const label = checkbox.closest('label')
    if (label) {
      label.classList.toggle('selected', checkbox.checked)
    }
  }

  // Handle city selection change - load districts
  async cityChanged() {
    const selectedCities = this.getSelectedCities()

    // Load districts for selected cities
    if (this.hasDistrictSelectTarget && selectedCities.length > 0) {
      try {
        const url = `${this.districtsUrlValue}?cities=${encodeURIComponent(selectedCities.join(','))}`
        const response = await fetch(url)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const districts = await response.json()

        // Clear existing options and rebuild using DOM API (XSS safe)
        this.districtSelectTarget.innerHTML = ''
        districts.forEach(d => {
          const option = document.createElement('option')
          option.value = d.code // names repeat across cities; the code is unique
          option.textContent = `${d.city} - ${d.name}`
          this.districtSelectTarget.appendChild(option)
        })
      } catch (error) {
        console.error('Failed to load districts:', error)
      }
    } else if (this.hasDistrictSelectTarget) {
      this.districtSelectTarget.innerHTML = ''
    }

    this.debouncedUpdateRecipientCount()
  }

  // Handle district selection change
  districtChanged() {
    this.debouncedUpdateRecipientCount()
  }

  // Debounced recipient count update
  debouncedUpdateRecipientCount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = setTimeout(() => {
      this.updateRecipientCount()
    }, this.debounceMsValue)
  }

  // Fetch and display recipient count
  async updateRecipientCount() {
    if (!this.hasRecipientCountTarget) return

    try {
      const params = this.buildTargetingParams()
      const url = `${this.previewUrlValue}?${params}`
      const response = await fetch(url)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()

      this.recipientCountTarget.textContent = data.count.toLocaleString()

      // Update submit button state
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = data.count === 0
      }
    } catch (error) {
      console.error('Failed to fetch recipient count:', error)
      this.recipientCountTarget.textContent = '-'
    }
  }

  // Build URL params for targeting preview
  buildTargetingParams() {
    const params = new URLSearchParams()

    // Get selected audience type
    const audienceType = this.getSelectedAudienceType()
    params.append('audience_type', audienceType)

    // Add city/district params based on type
    if (audienceType === 'by_city' || audienceType === 'by_district') {
      const cities = this.getSelectedCities()
      cities.forEach(city => params.append('target_cities[]', city))
    }

    if (audienceType === 'by_district') {
      const districts = this.getSelectedDistricts()
      districts.forEach(district => params.append('target_districts[]', district))
    }

    return params.toString()
  }

  // Get selected audience type from radio buttons
  getSelectedAudienceType() {
    const checked = this.element.querySelector('input[name="audience_type"]:checked')
    return checked ? checked.value : 'all_users'
  }

  // Get selected cities from multi-select
  getSelectedCities() {
    if (!this.hasCitySelectTarget) return []
    return Array.from(this.citySelectTarget.selectedOptions).map(opt => opt.value)
  }

  // Get selected districts from multi-select
  getSelectedDistricts() {
    if (!this.hasDistrictSelectTarget) return []
    return Array.from(this.districtSelectTarget.selectedOptions).map(opt => opt.value)
  }

  // Update message preview as user types
  updatePreview() {
    if (this.hasPreviewTitleTarget && this.hasTitleInputTarget) {
      const title = this.titleInputTarget.value.trim()
      // Use data-placeholder for I18n support (falls back to element's initial content)
      const titlePlaceholder = this.previewTitleTarget.dataset.placeholder || this.previewTitleTarget.textContent
      this.previewTitleTarget.textContent = title || titlePlaceholder
    }

    if (this.hasPreviewBodyTarget && this.hasBodyInputTarget) {
      const body = this.bodyInputTarget.value.trim()
      // Use data-placeholder for I18n support (falls back to element's initial content)
      const bodyPlaceholder = this.previewBodyTarget.dataset.placeholder || this.previewBodyTarget.textContent
      this.previewBodyTarget.textContent = body || bodyPlaceholder
    }
  }
}
